import React from "react";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
  closestCorners,
  DragOverlay,
} from "@dnd-kit/core";
import { fmtCompact } from "@/lib/format";

function DealCard({ deal, contact, company, onClick, dragging = false }) {
  return (
    <div
      onClick={onClick}
      data-testid={`deal-card-${deal.id}`}
      className={`p-3.5 rounded-xl bg-[#FDFCFB] border border-[#E8E6E1] hover:border-[#1A1A1A]/30 cursor-pointer shadow-[0_1px_0_rgba(0,0,0,0.02)] transition-all ${
        dragging ? "opacity-50" : ""
      }`}
    >
      <div className="text-[13.5px] text-[#1A1A1A] font-medium tracking-tight leading-snug line-clamp-2">
        {deal.title}
      </div>
      <div className="mt-2 flex items-center justify-between">
        <div className="font-mono-tech text-[11px] text-[#1A1A1A]">
          {fmtCompact(deal.value, deal.currency)}
        </div>
        {deal.status !== "open" && (
          <span
            className={`font-mono-tech text-[9px] tracking-[0.18em] uppercase px-1.5 py-0.5 rounded ${
              deal.status === "won" ? "bg-[#7EE787]/20 text-[#1f6e35]" : "bg-[#B03A2E]/10 text-[#B03A2E]"
            }`}
          >
            {deal.status}
          </span>
        )}
      </div>
      {(contact?.name || company?.name) && (
        <div className="mt-2 text-[11px] text-[#6B6A68] truncate">
          {[contact?.name, company?.name].filter(Boolean).join(" · ")}
        </div>
      )}
    </div>
  );
}

function DraggableCard({ id, children }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{ touchAction: "manipulation" }}
      className={isDragging ? "opacity-0" : ""}
    >
      {children}
    </div>
  );
}

function DroppableColumn({ stage, deals, contactsById, companiesById, onCardClick }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.id });
  const total = deals.reduce((s, d) => s + Number(d.value || 0), 0);
  return (
    <div
      ref={setNodeRef}
      data-testid={`kanban-column-${stage.id}`}
      className={`flex-shrink-0 w-[280px] flex flex-col rounded-2xl bg-[#F5F4F0] border ${
        isOver ? "border-[#1A1A1A]/40 bg-[#EDEAE2]" : "border-transparent"
      } transition-colors`}
    >
      <div className="px-4 pt-4 pb-3 flex items-center justify-between">
        <div>
          <div className="font-serif-display text-[15px] tracking-tight text-[#1A1A1A]">
            {stage.name}
          </div>
          <div className="font-mono-tech text-[10px] tracking-[0.16em] uppercase text-[#6B6A68] mt-0.5">
            {deals.length} · {fmtCompact(total, deals[0]?.currency || "INR")}
          </div>
        </div>
        {(stage.won || stage.lost) && (
          <span
            className={`font-mono-tech text-[9px] tracking-[0.18em] uppercase px-1.5 py-0.5 rounded ${
              stage.won ? "bg-[#7EE787]/30 text-[#1f6e35]" : "bg-[#B03A2E]/10 text-[#B03A2E]"
            }`}
          >
            {stage.won ? "Won" : "Lost"}
          </span>
        )}
      </div>
      <div className="px-3 pb-3 space-y-2 flex-1 min-h-[160px] max-h-[calc(100vh-260px)] overflow-y-auto">
        {deals.map((d) => (
          <DraggableCard key={d.id} id={d.id}>
            <DealCard
              deal={d}
              contact={contactsById[d.contact_id]}
              company={companiesById[d.company_id]}
              onClick={() => onCardClick(d)}
            />
          </DraggableCard>
        ))}
      </div>
    </div>
  );
}

export default function KanbanBoard({
  pipeline,
  deals,
  contacts,
  companies,
  onCardClick,
  onMove,
}) {
  const [activeId, setActiveId] = React.useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const contactsById = React.useMemo(
    () => Object.fromEntries(contacts.map((c) => [c.id, c])),
    [contacts]
  );
  const companiesById = React.useMemo(
    () => Object.fromEntries(companies.map((c) => [c.id, c])),
    [companies]
  );

  const dealsByStage = React.useMemo(() => {
    const map = {};
    pipeline.stages.forEach((s) => (map[s.id] = []));
    deals.forEach((d) => {
      (map[d.stage_id] || (map[d.stage_id] = [])).push(d);
    });
    Object.values(map).forEach((arr) =>
      arr.sort((a, b) => (a.position || 0) - (b.position || 0))
    );
    return map;
  }, [deals, pipeline]);

  const activeDeal = activeId ? deals.find((d) => d.id === activeId) : null;

  const handleDragEnd = (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;
    const movedDeal = deals.find((d) => d.id === active.id);
    if (!movedDeal) return;
    const targetStageId = over.id;
    if (movedDeal.stage_id === targetStageId) return;
    onMove?.(movedDeal, targetStageId);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={(e) => setActiveId(e.active.id)}
      onDragCancel={() => setActiveId(null)}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 overflow-x-auto px-6 md:px-10 py-6 no-scrollbar">
        {pipeline.stages.map((s) => (
          <DroppableColumn
            key={s.id}
            stage={s}
            deals={dealsByStage[s.id] || []}
            contactsById={contactsById}
            companiesById={companiesById}
            onCardClick={onCardClick}
          />
        ))}
      </div>
      <DragOverlay>
        {activeDeal ? (
          <DealCard
            deal={activeDeal}
            contact={contactsById[activeDeal.contact_id]}
            company={companiesById[activeDeal.company_id]}
            dragging
            onClick={() => {}}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
